import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreatePromotionCampaignPayload,
  SalesApiService,
  UpdatePromotionCampaignPayload
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-campaign-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, CurrencySelectorComponent, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.campaigns.form.createTitle') : t('sales.campaigns.form.editTitle')" [description]="t('sales.campaigns.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/sales/campaigns']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>
        <section class="card grid">
          <label><span>{{ t('sales.campaigns.fields.code') }}</span><input pInputText [(ngModel)]="form.code" [readonly]="!isCreateMode()" /></label>
          <label><span>{{ t('sales.campaigns.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
          <label><span>{{ t('sales.campaigns.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
          <label><span>{{ t('sales.campaigns.fields.budget') }}</span><p-inputNumber [(ngModel)]="form.budgetAmount" [min]="0" /></label>
          <label><span>{{ t('sales.campaigns.fields.validFrom') }}</span><ain-date-input [(ngModel)]="form.validFromUtc" /></label>
          <label><span>{{ t('sales.campaigns.fields.validTo') }}</span><ain-date-input [(ngModel)]="form.validToUtc" /></label>
          @if (!isCreateMode()) {
            <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.campaigns.active') }}</span></label>
            <p class="hint">{{ t('sales.campaigns.fields.consumed') }}: <strong>{{ form.budgetConsumed }}</strong></p>
          }
        </section>
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; margin-bottom: 10px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .hint { margin: 0; color: #64748b; font-size: 13px; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesCampaignDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');

  form = {
    code: '',
    name: '',
    currencyCode: 'IRR',
    budgetAmount: null as number | null,
    budgetConsumed: 0,
    validFromUtc: null as string | null,
    validToUtc: null as string | null,
    isActive: true
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('campaignId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) {
      this.form.validFromUtc = new Date().toISOString();
      this.loading.set(false);
      return;
    }
    this.api.getPromotionCampaign(id!).subscribe({
      next: row => {
        this.form = {
          code: row.code,
          name: row.name,
          currencyCode: row.currencyCode,
          budgetAmount: row.budgetAmount ?? null,
          budgetConsumed: row.budgetConsumed,
          validFromUtc: row.validFromUtc,
          validToUtc: row.validToUtc ?? null,
          isActive: row.isActive
        };
        this.loading.set(false);
      },
      error: () => { this.errorMessage.set(this.t('sales.campaigns.errorLoad')); this.loading.set(false); }
    });
  }

  save(): void {
    this.saving.set(true);
    const validity = {
      validFromUtc: this.form.validFromUtc ?? undefined,
      validToUtc: this.form.validToUtc ?? undefined,
      budgetAmount: this.form.budgetAmount
    };
    if (this.isCreateMode()) {
      const payload: CreatePromotionCampaignPayload = {
        code: this.form.code,
        name: this.form.name,
        currencyCode: this.form.currencyCode,
        ...validity
      };
      this.api.createPromotionCampaign(payload).subscribe({
        next: row => void this.router.navigate(['/sales/campaigns', row.id]),
        error: () => this.failSave()
      });
      return;
    }
    const id = this.route.snapshot.paramMap.get('campaignId')!;
    const payload: UpdatePromotionCampaignPayload = {
      name: this.form.name,
      currencyCode: this.form.currencyCode,
      isActive: this.form.isActive,
      ...validity
    };
    this.api.updatePromotionCampaign(id, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => this.failSave()
    });
  }

  private failSave(): void { this.errorMessage.set(this.t('sales.campaigns.errorSave')); this.saving.set(false); }
  t(key: string): string { return this.localization.translate(key); }
}
